'use client';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// ShoppingBag removed — product shop / cart disabled (service booking unaffected).
import { Menu, User, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

import Link from 'next/link';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';

const navItems = [
  { label: 'Services', href: '/#services' },
  { label: 'Technology', href: '/technology' },
  { label: 'Benefits', href: '/benefits' },
  { label: 'How It Works', href: '/#process' },
  // Product shop disabled — { label: 'Shop', href: '/products' },
  { label: 'Results', href: '/customer-results' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Careers', href: '/careers' },
];

export function Header() {
  // All hooks must be called before any conditional returns
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, isAuthenticated } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide header on admin routes, executioner routes, and printable invoice
  // pages (which need a clean canvas so the PDF/print output doesn't include
  // site chrome).
  const isAdminRoute = pathname?.startsWith('/admin');
  const isExecutionerRoute = pathname?.startsWith('/executioner');
  const isInvoiceRoute = pathname?.startsWith('/invoice');
  // Ads landing pages (/go) run headerless on purpose: a paid click should
  // see one goal and no site-nav exit paths.
  const isAdsLandingRoute = pathname?.startsWith('/go');
  const isTechPaymentPreview = pathname?.startsWith('/tech/payment-ux-preview');

  // Navbar shows customer login state only; admin/executioner stay separate
  const isCustomerContext = isAuthenticated && user?.role === 'customer';

  // Return null after all hooks have been called
  if (
    isAdminRoute ||
    isExecutionerRoute ||
    isInvoiceRoute ||
    isAdsLandingRoute ||
    isTechPaymentPreview
  ) {
    return null;
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
    }
  };

  return (
    <>
      <motion.header data-component-id="src/components/Header.tsx:76:6" data-component-path="src/components/Header.tsx" data-component-line="76" data-component-end-line="172" data-component-file="Header.tsx" data-component-name="motion.header" data-component-content="%7B%22elementName%22%3A%22motion.header%22%7D"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/70 backdrop-blur-2xl backdrop-saturate-[1.8] shadow-[0_1px_0_rgba(0,0,0,0.04)] py-1'
            : 'bg-white/0 py-3'
        }`}
        data-testid="main-header"
      >
        <nav data-component-id="src/components/Header.tsx:87:8" data-component-path="src/components/Header.tsx" data-component-line="87" data-component-end-line="171" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22container-main%22%7D" className="container-main">
          <div data-component-id="src/components/Header.tsx:88:10" data-component-path="src/components/Header.tsx" data-component-line="88" data-component-end-line="170" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
            {/* Logo */}
            <Link data-component-id="src/components/Header.tsx:90:12" data-component-path="src/components/Header.tsx" data-component-line="90" data-component-end-line="105" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22relative%20z-10%22%7D" href="/" className="relative z-10" data-testid="logo-link">
              <motion.div data-component-id="src/components/Header.tsx:91:14" data-component-path="src/components/Header.tsx" data-component-line="91" data-component-end-line="104" data-component-file="Header.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22flex%20items-center%22%7D"
                whileHover={{ opacity: 0.7 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center"
              >
                <div data-component-id="src/components/Header.tsx:96:16" data-component-path="src/components/Header.tsx" data-component-line="96" data-component-end-line="103" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-%5B168px%5D%20h-%5B48px%5D%20md%3Aw-%5B224px%5D%20md%3Ah-%5B56px%5D%22%7D" className="relative w-[168px] h-[48px] md:w-[224px] md:h-[56px]">
                  <Image data-component-id="src/components/Header.tsx:97:18" data-component-path="src/components/Header.tsx" data-component-line="97" data-component-end-line="102" data-component-file="Header.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fmagia-logo-dark.png%22%2C%22alt%22%3A%22Magia%22%2C%22className%22%3A%22object-contain%20object-left%22%7D"
                    src="/images/magia-logo-dark.png"
                    alt="Magia"
                    fill
                    className="object-contain object-left"
                  />
                </div>
              </motion.div>
            </Link>

            {/* Desktop Navigation - Apple minimal style */}
            <div data-component-id="src/components/Header.tsx:108:12" data-component-path="src/components/Header.tsx" data-component-line="108" data-component-end-line="119" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20gap-9%22%7D" className="hidden lg:flex items-center gap-9">
              {navItems.map((item) => (
                <Link data-component-id="src/components/Header.tsx:110:16" data-component-path="src/components/Header.tsx" data-component-line="110" data-component-end-line="117" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-%5B13px%5D%20font-medium%20text-%5B%231D1D1F%5D%2F80%20hover%3Atext-%5B%231D1D1F%5D%20transition-opacity%20duration-200%22%7D"
                  key={item.label}
                  href={item.href}
                  className="text-[13px] font-medium text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-opacity duration-200"
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div data-component-id="src/components/Header.tsx:122:12" data-component-path="src/components/Header.tsx" data-component-line="122" data-component-end-line="169" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
              {/* Cart disabled — product buying removed (service booking unaffected).
              <motion.button
                onClick={() => router.push('/cart')}
                className="p-2.5 rounded-full hover:bg-black/[0.04] transition-colors duration-200"
                data-testid="cart-btn"
                whileTap={{ scale: 0.92 }}
              >
                <ShoppingBag className="w-[18px] h-[18px] text-[#1D1D1F]" />
              </motion.button>
              */}

              {isAuthenticated ? (
                <motion.button data-component-id="src/components/Header.tsx:135:16" data-component-path="src/components/Header.tsx" data-component-line="135" data-component-end-line="150" data-component-file="Header.tsx" data-component-name="motion.button" data-component-content="%7B%22elementName%22%3A%22motion.button%22%2C%22className%22%3A%22hidden%20sm%3Aflex%20items-center%20justify-center%20w-9%20h-9%20rounded-full%20bg-%5B%23204653%5D%20text-white%20text-xs%20font-semibold%22%7D"
                  onClick={() =>
                    router.push(
                      user?.role === 'admin'
                        ? '/admin'
                        : user?.role === 'executioner'
                          ? '/executioner-mobile-dashboard'
                          : '/customer-dashboard'
                    )
                  }
                  className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-[#204653] text-white text-xs font-semibold"
                  data-testid="account-btn"
                  whileTap={{ scale: 0.92 }}
                >
                  {(user?.name || user?.id || '?').trim().charAt(0).toUpperCase()}
                </motion.button>
              ) : (
                <Link data-component-id="src/components/Header.tsx:152:16" data-component-path="src/components/Header.tsx" data-component-line="152" data-component-end-line="158" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Flogin%22%2C%22className%22%3A%22hidden%20sm%3Aflex%20items-center%20justify-center%20px-5%20py-2%20bg-%5B%23204653%5D%20text-white%20text-%5B13px%5D%20font-medium%20rounded-full%20hover%3Abg-%5B%231a3a44%5D%20transition-colors%20duration-200%22%2C%22textContent%22%3A%22Sign%20In%22%7D"
                  href="/login"
                  className="hidden sm:flex items-center justify-center px-5 py-2 bg-[#204653] text-white text-[13px] font-medium rounded-full hover:bg-[#1a3a44] transition-colors duration-200"
                  data-testid="login-link"
                >
                  Sign In
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button data-component-id="src/components/Header.tsx:162:14" data-component-path="src/components/Header.tsx" data-component-line="162" data-component-end-line="168" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20p-2.5%20rounded-full%20hover%3Abg-black%2F%5B0.04%5D%20transition-colors%20duration-200%22%7D"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-full hover:bg-black/[0.04] transition-colors duration-200"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X data-component-id="src/components/Header.tsx:167:36" data-component-path="src/components/Header.tsx" data-component-line="167" data-component-end-line="167" data-component-file="Header.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" /> : <Menu data-component-id="src/components/Header.tsx:167:64" data-component-path="src/components/Header.tsx" data-component-line="167" data-component-end-line="167" data-component-file="Header.tsx" data-component-name="Menu" data-component-content="%7B%22elementName%22%3A%22Menu%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu - Apple sheet style */}
      <AnimatePresence data-component-id="src/components/Header.tsx:175:6" data-component-path="src/components/Header.tsx" data-component-line="175" data-component-end-line="234" data-component-file="Header.tsx" data-component-name="AnimatePresence" data-component-content="%7B%22elementName%22%3A%22AnimatePresence%22%7D">
        {isMobileMenuOpen && (
          <>
            <motion.div data-component-id="src/components/Header.tsx:178:12" data-component-path="src/components/Header.tsx" data-component-line="178" data-component-end-line="185" data-component-file="Header.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22fixed%20inset-0%20z-40%20bg-black%2F30%20backdrop-blur-sm%20lg%3Ahidden%22%7D"
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-menu-overlay"
            />
            <motion.div data-component-id="src/components/Header.tsx:186:12" data-component-path="src/components/Header.tsx" data-component-line="186" data-component-end-line="231" data-component-file="Header.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22fixed%20top-0%20right-0%20bottom-0%20w-%5B85%25%5D%20max-w-sm%20z-50%20bg-white%2F95%20backdrop-blur-2xl%20lg%3Ahidden%22%7D"
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm z-50 bg-white/95 backdrop-blur-2xl lg:hidden"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              data-testid="mobile-menu"
            >
              <div data-component-id="src/components/Header.tsx:194:14" data-component-path="src/components/Header.tsx" data-component-line="194" data-component-end-line="230" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20h-full%20pt-20%20pb-8%20px-6%22%7D" className="flex flex-col h-full pt-20 pb-8 px-6">
                <nav data-component-id="src/components/Header.tsx:195:16" data-component-path="src/components/Header.tsx" data-component-line="195" data-component-end-line="212" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex-1%20space-y-0%22%7D" className="flex-1 space-y-0">
                  {navItems.map((item, index) => (
                    <motion.div data-component-id="src/components/Header.tsx:197:20" data-component-path="src/components/Header.tsx" data-component-line="197" data-component-end-line="210" data-component-file="Header.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%7D"
                      key={item.label}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index }}
                    >
                      <Link data-component-id="src/components/Header.tsx:203:22" data-component-path="src/components/Header.tsx" data-component-line="203" data-component-end-line="209" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22block%20py-4%20text-%5B17px%5D%20font-medium%20text-%5B%231D1D1F%5D%20hover%3Atext-%5B%23204653%5D%20transition-colors%20border-b%20border-black%2F%5B0.06%5D%22%7D"
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-4 text-[17px] font-medium text-[#1D1D1F] hover:text-[#204653] transition-colors border-b border-black/[0.06]"
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  ))}
                </nav>

                {!isAuthenticated && (
                  <motion.div data-component-id="src/components/Header.tsx:215:18" data-component-path="src/components/Header.tsx" data-component-line="215" data-component-end-line="228" data-component-file="Header.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22pt-6%20border-t%20border-black%2F%5B0.06%5D%22%7D"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="pt-6 border-t border-black/[0.06]"
                  >
                    <Link data-component-id="src/components/Header.tsx:221:20" data-component-path="src/components/Header.tsx" data-component-line="221" data-component-end-line="227" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Flogin%22%2C%22className%22%3A%22btn-primary%20w-full%20justify-center%22%2C%22textContent%22%3A%22Sign%20In%22%7D"
                      href="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="btn-primary w-full justify-center"
                    >
                      Sign In
                    </Link>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
